// @ts-nocheck
import React, { useContext, useEffect, useState } from 'react';
import format from 'date-fns/format'
import {
  useNavigate, useParams, useSearchParams,
} from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import StreamContext from 'utils/StreamContext'
import { ReactComponent as IconArrowRight } from 'images/arrow-right.svg';
import { getFeedGroup } from 'sdk/utils/feed';
import Page from 'modules/app/components/Page';
import PostView from 'components/shared/PostView';
import { DateInput } from 'components/shared/Input';
import Skeleton from 'components/shared/Skeleton';
import PageContainer from 'components/shared/PageContainer';
import clsx from 'clsx';
import NoContent from 'components/shared/NoContent/NoContent';
import useGetAllTradingPlatforms from 'sdk/hooks/trading/useGetAllTradingPlatforms'
import useUserPosts from 'sdk/hooks/user/useUserPosts'
import Button from 'components/shared/Button';
import CompareTradingModal from 'modules/assets/components/CompareTradingModal/CompareTradingModal';
import styles from './TradingList.module.scss';

function TradingListSkeleton({ title }: { title: string }) {
  return (
    <PageContainer className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.h}>
          <div className={styles.title}>
            <IconArrowRight />
            {title ? (
              <span>{title}</span>
            ) : (
              <Skeleton style={{ width: '300px', display: 'inline-block' }} />
            )}
          </div>
        </div>
      </div>
      <div className={styles.playlistPosts}>
        {Array.from(Array(12).keys()).map((item: number) => (
          <div className={styles.playlistItem} key={item}>
            <Skeleton className={styles.playlistItemSkeleton} />
          </div>
        ))}
      </div>
    </PageContainer>
  )
}

function TradingList() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const asset = searchParams.get('asset')
  const playlistData = useGetAllTradingPlatforms()
  const isDaily = id === '5'
  const [activeCategory] = useState<string | null>(null)
  const { client } = useContext(StreamContext)
  const [, setFeed] = useState<any>()
  const feedName = 'global_flat'
  const { ref, inView } = useInView()
  const [day, setDay] = useState<Date | null>(new Date())
  const [posts, setPosts] = useState<any>([])
  const [loaded, setLoaded] = useState<boolean>(false)
  const [activePostId, setActivePostId] = useState('')
  const navigate = useNavigate()
  const [isOpenCompareModal, setIsOpenCompareModal] = useState(false)
  const [mappedPosts, setMappedPosts] = useState<any[]>([])

  useEffect(() => {
    if (client) {
      const filters: string[] = []
      if (activeCategory) {
        filters.push(`cat-${activeCategory}`)
      }
      if (asset) {
        filters.push(`asset-${asset}`)
      }
      if (id) {
        filters.push(`playlist-${id}`)
      }
      if (isDaily && day) {
        filters.push(`date-${format(day || (new Date()), 'yyyy-MM-dd')}`)
      }
      setFeed(client.feed(feedName, getFeedGroup(filters)))
    }
  }, [client, activeCategory, day])

  const tradesPostsIds = playlistData.data ? playlistData.data.map((p) => p.post_id)
    .filter((p) => p) : []

  const feedData = useUserPosts(tradesPostsIds)

  useEffect(() => {
    if (feedData.isSuccess) {
      setLoaded(true)
      setPosts(feedData.data)
    }
  }, [feedData.isSuccess])

  useEffect(() => {
    if (posts) {
      const mapped = posts.map((p) => {
        const trade = playlistData?.data?.find((t) => t.post_id === p.id)
        if (trade) {
          return { ...p, tradingPlatform: trade }
        }
        return p
      })
      setMappedPosts(mapped)
    }
  }, [posts])

  useEffect(() => {
    if (inView && feedData.hasNextPage) {
      feedData.fetchNextPage()
    }
  }, [inView, feedData.hasNextPage])

  if (feedData.isLoading && !loaded) {
    return (<TradingListSkeleton title={playlistData?.data?.name || ''} />)
  }

  return (
    <PageContainer className={styles.container}>
      { playlistData?.data?.bg_image ? (
        <div
          className={styles.background}
          style={{ backgroundImage: `url(${playlistData?.data?.bg_image})` }}
        />
      ) : null }
      <div className={styles.headerRow}>
        <div className={styles.h} onClick={() => navigate(-1)}>
          { playlistData?.data?.bg_image ? (
            <div className={styles.title}>
              <IconArrowRight />
            </div>
          ) : (
            <h1 className={styles.title}>
              <IconArrowRight />
              Trading Platforms
            </h1>
          )}
        </div>
        <Button className={styles.button} onClick={() => setIsOpenCompareModal(true)}>
          Compare
        </Button>
        <CompareTradingModal
          isOpen={isOpenCompareModal}
          onClose={() => setIsOpenCompareModal(false)}
        />
        {isDaily ? (
          <DateInput
            className={styles.dayInput}
            value={day}
            onChange={(value: Date | null) => {
              setDay(value)
            }}
            dateFormat="MMM, dd"
          />
        ) : null}
      </div>
      { playlistData?.data?.bg_image ? (
        <h1 className={styles.titleBig}>
          {playlistData?.data?.name}
        </h1>
      ) : null }
      <div className={clsx(
        styles.playlistPosts,
        feedData.isLoading && styles.playlistPosts_loading,
      )}
      >
        {mappedPosts.map((post: any) => (
          <div
            className={styles.playlistItem}
            key={post?.id}
            onMouseEnter={() => setActivePostId(post?.id)}
            onMouseLeave={() => setActivePostId('')}
            onClick={() => navigate(`/tradingPlatforms/${post?.id}`)}
          >
            <PostView
              data={{ data: post }}
              isDaily={isDaily}
              isActive={post?.id === activePostId}
            />
          </div>
        ))}
      </div>
      <div ref={ref} />
      {!posts?.length && (
        <div className={feedData.isLoading ? styles.playlistPosts_loading : ''}>
          <NoContent
            message="No data satisfying the filters"
          />
        </div>
      )}
    </PageContainer>
  )
}

function TradingListPage() {
  return (
    <Page activeLink="/">
      <TradingList />
    </Page>
  )
}

export default TradingListPage
